/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { Dash, Layout, MarkerSymbol } from "plotly.js";
import {
  ChemicalElementSymbol,
  MaterialGroupName,
  MaterialGroupingName,
  NonElementQuantity,
} from "src/store/api/generatedApi";
import theme from "src/theme";

dayjs.extend(utc);

export const fractionTickFormatting = ",.1%";
export const generalTickFormatting = "~s";
export const chemicalElementColours = new Map<
  ChemicalElementSymbol | NonElementQuantity,
  string
>([
  ["Cu", theme.palette.data.orange],
  ["Sn", theme.palette.data.pink],
  ["Ni", theme.palette.data.lightblue],
  ["Cr", theme.palette.data.blue],
  ["Mo", theme.palette.data.green],
]);

const materialColours = [
  theme.palette.data.blue,
  theme.palette.data.pink,
  theme.palette.data.lightblue,
  theme.palette.data.orange,
  theme.palette.data.green,
  theme.palette.data.red,
  "#1f77b4",
  "#ff7f0e",
  "#2ca02c",
  "#d62728",
  "#9467bd",
  "#8c564b",
  "#e377c2",
  "#7f7f7f",
  "#bcbd22",
  "#17becf",
  "#aec7e8",
  "#ffbb78",
  "#98df8a",
  "#ff9896",
  "#c5b0d5",
  "#c49c94",
  "#f7b6d2",
  "#c7c7c7",
  "#dbdb8d",
  "#9edae5",
];

const lineDashes: Dash[] = [
  "solid",
  "dot",
  "dash",
  "longdash",
  "dashdot",
  "longdashdot",
];
const markerSymbols: MarkerSymbol[] = [
  "circle",
  "square",
  "diamond",
  "triangle-up",
  "triangle-down",
  "triangle-right",
];

interface MaterialGroupAndGrouping {
  groupingName: MaterialGroupingName;
  groupName: MaterialGroupName;
}

export const buildMaterialColours = (
  materialGroupAndGroupings: MaterialGroupAndGrouping[]
): Map<string, string> => {
  const groupingCounters = new Map<MaterialGroupingName, number>();
  const map = new Map<string, string>();

  for (const { groupingName, groupName } of materialGroupAndGroupings) {
    const counter = groupingCounters.get(groupingName) ?? 0;
    const key = JSON.stringify({ groupingName, groupName });
    if (map.has(key)) {
      continue;
    }
    const colour =
      materialColours[counter % materialColours.length] ??
      theme.palette.grey[500];
    map.set(key, colour);
    groupingCounters.set(groupingName, counter + 1);
  }
  return map;
};

export const getMaterialColour = (
  materialColours: Map<string, string>,
  groupingName: MaterialGroupingName,
  groupName: MaterialGroupName
) => {
  const key = JSON.stringify({ groupingName, groupName });
  return materialColours.get(key);
};

export const getColorBySource = (sources: string[], source: string) => {
  const index = sources.indexOf(source);
  if (index < 0 || index >= materialColours.length) {
    return materialColours[0];
  }
  return materialColours[index];
};

export const getLineDash = (index: number) => {
  return lineDashes[index % lineDashes.length];
};

export const getMarkerSymbol = (index: number) => {
  return markerSymbols[index % markerSymbols.length];
};

export const defaultLayout: Partial<Layout> = {
  hovermode: "x unified",
  margin: {
    t: 10,
    r: 0,
  },
  legend: {
    x: 0.5,
    y: -0.2,
    orientation: "h",
    xanchor: "center",
    yanchor: "top",
    groupclick: "toggleitem",
  },
  autosize: true,
};

export const setSelectedDateFromPlotClick = (
  setSelectedDate: (date: Date) => void
): ((event: Plotly.PlotMouseEvent) => void) => {
  return (event: Plotly.PlotMouseEvent) => {
    if (event.points && event.points.length > 0) {
      const point = event.points[0];
      if (point?.x) {
        const clickedDate = dayjs.utc(point.x).startOf("day").toDate();
        setSelectedDate(clickedDate);
      }
    }
  };
};
